import { Component, afterNextRender, computed, inject, signal } from '@angular/core';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map } from 'rxjs';
import { LucideAngularModule } from 'lucide-angular';
import { ButtonComponent } from '@mentebonita/ui';
import { THALIQ_WIDGET_CONFIG, ThaliqWidgetConfig } from '@mentebonita/core';
import { SofiaConsentService } from '../../services/sofia-consent.service';
import { ThaliqEngineService } from '../../services/thaliq-engine.service';
import { ThaliqV4WidgetService } from '../../services/thaliq-v4-widget.service';

/** Lo que expone el loader de Thaliq en `window` una vez cargado. */
interface ThaliqWidgetApi {
  open?: () => void;
}

/** Mismo selector que usa ThaliqWidgetLoaderService para encontrar el iframe. */
const WIDGET_SELECTOR = 'iframe[data-thaliq-widget], iframe[title="Thaliq chat"]';
/** Cuánto esperar a que el widget recién activado esté listo para abrirlo. */
const OPEN_TIMEOUT_MS = 15000;
const OPEN_POLL_MS = 250;

/**
 * Sofía en la web pública, antes de que la persona le dé permiso.
 *
 * Es un botón propio: no carga nada de Thaliq. Al tocarlo explica qué es Sofía
 * y pide el permiso; si la persona acepta, se carga el widget real y se abre.
 * Desde el pie de página («Permiso de Sofía») también sirve para retirarlo.
 * En el portal del paciente no aparece: ahí Sofía carga sin pedir permiso.
 */
@Component({
  selector: 'app-sofia-invite',
  standalone: true,
  imports: [LucideAngularModule, ButtonComponent, RouterLink],
  templateUrl: './sofia-invite.component.html',
  styleUrl: './sofia-invite.component.css',
})
export class SofiaInviteComponent {
  protected readonly consent = inject(SofiaConsentService);
  private readonly router = inject(Router);
  private readonly engine = inject(ThaliqEngineService);
  private readonly v4 = inject(ThaliqV4WidgetService);
  private readonly cfg = inject<ThaliqWidgetConfig | null>(THALIQ_WIDGET_CONFIG, {
    optional: true,
  });

  private readonly hydrated = signal(false);
  protected readonly promptOpen = signal(false);
  protected readonly title = this.cfg?.title ?? 'Sofía';
  protected readonly color = this.cfg?.primary ?? 'var(--mb-color-brand-secondary-medium)';

  private readonly url = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => event.urlAfterRedirects)
    ),
    { initialValue: this.router.url }
  );

  /** Con permiso dado, solo aparece si la persona lo abrió desde el pie de página. */
  protected readonly visible = computed(() => {
    if (!this.hydrated() || !this.cfg?.enabled) {
      return false;
    }
    const url = this.url();
    if (this.consent.isPortal(url) || (this.cfg.blacklist ?? []).some((route) => url.startsWith(route))) {
      return false;
    }
    return !this.consent.accepted() || this.consent.showSettings();
  });

  /** El panel se ve si la persona tocó la burbuja o lo abrió desde el pie de página. */
  protected readonly panelOpen = computed(() => this.promptOpen() || this.consent.showSettings());

  constructor() {
    afterNextRender(() => this.hydrated.set(true));
  }

  protected togglePrompt(): void {
    this.promptOpen.update((open) => !open);
  }

  protected dismiss(): void {
    this.promptOpen.set(false);
    this.consent.closeSettings();
  }

  protected activate(): void {
    this.promptOpen.set(false);
    this.consent.accept();
    this.openWhenReady();
  }

  protected revoke(): void {
    this.promptOpen.set(false);
    this.consent.revoke();
  }

  /**
   * El widget carga async: hay que esperar a que exista el iframe y la API
   * antes de abrirlo. Si no llega a tiempo, queda la burbuja real a la vista.
   */
  private openWhenReady(): void {
    if (this.engine.isV4()) {
      this.v4.openWhenReady();
      return;
    }
    const startedAt = Date.now();
    const timer = setInterval(() => {
      const api = (window as unknown as { ThaliqWidget?: ThaliqWidgetApi }).ThaliqWidget;
      const frame = document.querySelector(WIDGET_SELECTOR);
      if (frame && typeof api?.open === 'function') {
        clearInterval(timer);
        api.open();
        return;
      }
      if (Date.now() - startedAt > OPEN_TIMEOUT_MS) {
        clearInterval(timer);
      }
    }, OPEN_POLL_MS);
  }
}
